/**
 * The links you made, remembered on this device so the Share sheet can show one again: the server
 * keeps only a hash of each token, so it can't. Kept per account and recipe, and shown only while
 * it's the link that's on (the server says which by its id); a replaced or turned-off link is
 * dropped when the sheet sees it. Signing out forgets them all. Also remembers your last "Show my
 * name" choice for the next link (on at first). Without storage nothing is remembered.
 */
import { SHARE_TOKEN_PATTERN, SHARED_RECIPE_PATH } from '../../shared/sharing';

export const SHARE_LINKS_KEY = 'grams.shareLinks';
export const SHOW_NAME_KEY = 'grams.shareShowName';
/** Plenty for one person's links on (at most 100), across the accounts that use this device. */
const MAX_REMEMBERED = 200;

/** The part of Web Storage used here, so tests can pass their own. */
export interface LinkStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

interface Remembered {
  uid: string;
  recipeId: string;
  linkId: string;
  url: string;
}

function deviceStore(): LinkStore | undefined {
  try {
    return (globalThis as { localStorage?: LinkStore }).localStorage;
  } catch {
    // Reading localStorage itself throws where storage is blocked.
    return undefined;
  }
}

/** Only addresses shaped like a shared link are ever shown or copied. */
function isShareUrl(url: string): boolean {
  const at = url.indexOf(`${SHARED_RECIPE_PATH}#r=`);
  return /^https?:\/\//.test(url) && at > 0 && SHARE_TOKEN_PATTERN.test(url.slice(at + SHARED_RECIPE_PATH.length + 3));
}

function readAll(store: LinkStore | undefined): Remembered[] {
  try {
    const value: unknown = JSON.parse(store?.getItem(SHARE_LINKS_KEY) ?? '[]');
    if (!Array.isArray(value)) return [];
    return value.filter(
      (r): r is Remembered =>
        typeof r === 'object' &&
        r !== null &&
        typeof r.uid === 'string' &&
        typeof r.recipeId === 'string' &&
        typeof r.linkId === 'string' &&
        typeof r.url === 'string' &&
        isShareUrl(r.url),
    );
  } catch {
    return [];
  }
}

function writeAll(list: Remembered[], store: LinkStore | undefined): void {
  try {
    if (list.length === 0) store?.removeItem(SHARE_LINKS_KEY);
    else store?.setItem(SHARE_LINKS_KEY, JSON.stringify(list.slice(-MAX_REMEMBERED)));
  } catch {
    // Full or blocked storage: the link shows until the sheet closes, just not later.
  }
}

/** The address of the recipe's link that's on (`linkId`), when this device made it. */
export function rememberedLink(uid: string, recipeId: string, linkId: string | null, store: LinkStore | undefined = deviceStore()): string | null {
  const found = readAll(store).find((r) => r.uid === uid && r.recipeId === recipeId);
  return found && found.linkId === linkId ? found.url : null;
}

/** Drops what's remembered for the recipe unless it's the link that's on (`linkId`): a replaced or turned-off link doesn't work anyway. */
export function dropStaleLink(uid: string, recipeId: string, linkId: string | null, store: LinkStore | undefined = deviceStore()): void {
  const all = readAll(store);
  const kept = all.filter((r) => !(r.uid === uid && r.recipeId === recipeId && r.linkId !== linkId));
  if (kept.length !== all.length) writeAll(kept, store);
}

export function rememberLink(uid: string, recipeId: string, linkId: string, url: string, store: LinkStore | undefined = deviceStore()): void {
  if (!isShareUrl(url)) return;
  const rest = readAll(store).filter((r) => !(r.uid === uid && r.recipeId === recipeId));
  writeAll([...rest, { uid, recipeId, linkId, url }], store);
}

/** Signing out: no link stays behind on this device. */
export function forgetAllLinks(store: LinkStore | undefined = deviceStore()): void {
  try {
    store?.removeItem(SHARE_LINKS_KEY);
  } catch {
    // Blocked storage: nothing was kept.
  }
}

/** Signing in: links another account made on this device don't stay around for this one. */
export function keepAccountLinks(uid: string, store: LinkStore | undefined = deviceStore()): void {
  const all = readAll(store);
  const mine = all.filter((r) => r.uid === uid);
  if (mine.length !== all.length) writeAll(mine, store);
}

/** "Show my name" for the next link: your last choice, on at first. */
export function lastShowName(store: LinkStore | undefined = deviceStore()): boolean {
  try {
    return store?.getItem(SHOW_NAME_KEY) !== 'off';
  } catch {
    return true;
  }
}

export function rememberShowName(on: boolean, store: LinkStore | undefined = deviceStore()): void {
  try {
    if (on) store?.removeItem(SHOW_NAME_KEY);
    else store?.setItem(SHOW_NAME_KEY, 'off');
  } catch {
    // Blocked storage: the switch still works for this link.
  }
}
